import { useState, useEffect } from 'react';
import useSelectionStore from '@store/useSelectionStore';
import useGameStore from '@store/useGameStore';
import { ClickableType, ClickContext } from '@utils/enums';
import { getAvailableActions } from '@api/actionApi';
import { enterTargetingMode } from '@services/actionService';
import { getModifierBreakdown } from '@utils/statCalculations';
import ActionIcon from '@components/ui/game/action-icon/ActionIcon';
import APDisplay from '@components/ui/game/ap-display/APDisplay';
import EnergyDisplay from '@components/ui/game/energy-display/EnergyDisplay';
import './ActionsPanel.css';

export default function ActionsPanel() {
  const { currentClick, clickContext } = useSelectionStore();
  const game = useGameStore(state => state.game);
  const isPlanPhase = useGameStore(state => state.isPlanPhase);
  const [availableActions, setAvailableActions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Only show if a unit is selected
  const showPanel = currentClick && currentClick.type === ClickableType.UNIT;

  // Fetch available actions when unit is selected (always show, disable if not in plan phase)
  useEffect(() => {
    if (!showPanel || !game || !currentClick.data?.id) {
      setAvailableActions([]);
      return;
    }

    const fetchActions = async () => {
      setLoading(true);
      try {
        const actions = await getAvailableActions(game.id, currentClick.data.id);

        if (!actions) {
          throw new Error('[ActionsPanel] No actions returned from API');
        }

        setAvailableActions(actions);
      } catch (error) {
        console.error('[ActionsPanel] Failed to fetch actions:', error);
        setAvailableActions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchActions();
  }, [showPanel, game?.id, currentClick?.data?.id]);

  if (!showPanel) {
    return null;
  }

  // Get fresh unit data
  const unit = useGameStore.getState().getUnitOrAvailableUnitById(currentClick.data.id);

  if (!unit) {
    console.warn('[ActionsPanel] Unit not found:', currentClick.data.id);
    return null;
  }

  // Check if actions should be disabled
  const isInDeployment = useGameStore.getState().isUnitInDeployment(unit.id);
  const actionsDisabled = !isPlanPhase || isInDeployment;

  // Validate unit has required data
  if (!unit.unit_template) {
    throw new Error(`[ActionsPanel] Unit ${unit.id} missing unit_template`);
  }

  if (!unit.unit_template.max_ap) {
    throw new Error(`[ActionsPanel] Unit template missing max_ap for unit ${unit.id}`);
  }

  // Calculate AP with effects like the old implementation
  const baseMaxAp = unit.unit_template.max_ap;
  const maxApBreakdown = getModifierBreakdown(unit, 'max_ap');
  const totalAp = maxApBreakdown.effective;

  // Use current AP but cap it to effective max
  const currentAp = unit.ap;
  const filledAp = Math.min(currentAp, totalAp);

  const handleActionClick = (action) => {
    if (!action) {
      throw new Error('[ActionsPanel] Action is null');
    }

    if (!action.slug) {
      throw new Error('[ActionsPanel] Action missing slug');
    }

    // console.log('[ActionsPanel] Action clicked:', action.slug);

    // Use enterTargetingMode from actionService
    enterTargetingMode(action, unit);
  };

  return (
    <div className="actions-panel">
      <div className="unit-ap-display">
        <div className="ap-breakdown">
          <APDisplay
            baseTotal={baseMaxAp}
            total={totalAp}
            filled={filledAp}
            size="small"
            unit={unit}
          />
        </div>
      </div>

      {/* {loading && (
        <div className="actions-loading">Loading actions...</div>
      )} */}

      {!loading && availableActions.length === 0 && (
        <div className="actions-empty">No actions available</div>
      )}

      {/* {actionsDisabled && availableActions.length > 0 && (
        <div className="actions-empty" style={{ color: '#fbbf24' }}>
          {!isPlanPhase ? 'Actions only available in Plan phase' : 'Unit must be deployed first'}
        </div>
      )} */}

      {!loading && availableActions.length > 0 && (
        <div className="actions-list">
          {availableActions.map(action => {
            if (!action) {
              console.warn('[ActionsPanel] Null action in list');
              return null;
            }

            const apCost = action.ap_cost !== undefined ? action.ap_cost : 0;
            const energyCost = action.energy_cost !== undefined ? action.energy_cost : 0;
            const canAfford = filledAp >= apCost;
            const isUsable = action.is_usable && canAfford && !actionsDisabled;
            const isTargeting = clickContext === ClickContext.TARGET_ACTION;

            return (
              <button
                key={action.slug}
                className={`action-button ${!isUsable ? 'disabled' : ''} ${isTargeting ? 'targeting' : ''}`}
                onClick={() => handleActionClick(action)}
                disabled={!isUsable}
                title={action.description}
              >
                <ActionIcon
                  action={action}
                  size={16}
                  teamClass={unit.teamClass}
                />
                <span className="action-name">{action.name}</span>
                <div className="action-costs">
                  {apCost > 0 && (
                    <APDisplay
                      baseTotal={apCost}
                      total={apCost}
                      filled={Math.min(apCost, filledAp)}
                      size="tiny"
                    />
                  )}
                  {energyCost > 0 && (
                    <EnergyDisplay
                      value={energyCost}
                      size="tiny"
                    />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}